<script>
import Base from './base.vue';
import { mapGetters, mapActions } from 'vuex';
import { computedCallingPlans, computedPbxBasePlans } from '@/common/service/computers/plans/pbx';
import {
  ZOOM_MEETING_PRO_MIN_LICENSE,
  BILLING_CYCLE,
  PlanDisabledStatusEnum,
} from '@/common/constants';
import { checkBizPlusAddonLicense } from '../utils';
import { PBX_REGIONS } from '@/plans/ZOOM_PHONE/helper/constants';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';
import plansMixin from '@/plans/mx/plan';
import { bundleRegions, zoneBundleMode } from '../helper';
import _ from 'lodash';
import { isEmptyVal } from '@/utils';

export default {
  name: 'TRIAL2PAID',
  extends: Base,
  mixins: [plansMixin],
  data() {
    return {
      isExpanded: true,
    };
  },
  computed: {
    ...mapGetters(['boughtPlans', 'basicConfig', 'getUserCurrentSubscriptions', 'couponSubscriptionLevelAction', 'assignInfo', 'getSelectedPlansWithQuantityMap']),
    pbxRegions() {
      return bundleRegions.filter((x) => {
        // for legacy customer who has been bought UK region
        const hasBought = this.currentBoughtBizPlusPlans.find(p => p.region === x.key);
        if (x.key === PBX_REGIONS.JP){
          return (!this.$config('hideZoomPhoneJPPlans') && !!hasBought && !this.$config('hideZoomPhoneJPPlansPhase2')) || this.$config('enableBizPlusJapanOnlineBuy');
        }
        if (this.$config('hideZoomPhoneUKIRPlans')) {
          if (hasBought) return true;
          return x.key !== PBX_REGIONS.UK_AND_IRELAND;
        }
        return true;
      })
      .filter(x => this.bundlePlans.find(item => item.region === x.key))
      .filter(zoneBundleMode.filter);
    },
    planBillInfo() {
      const basePlan = computedPbxBasePlans(this.boughtPlans)?.[0] ?? {};
      // if base plan is free trial, return false
      if (basePlan?.isFreeTrial) return false;
      const plan = computedCallingPlans(this.boughtPlans)?.[0] ?? {};
      const payment = getZPCallingFeesText(basePlan).title;
      const billingCycle = basePlan.billing_cycle === BILLING_CYCLE.PAY_AS_YOU_GO ? BILLING_CYCLE.MONTHLY : plan.billing_cycle;
      return {
        billing_cycle: billingCycle,
        payment
      }
    },
    planDetailLabel() {
      return '';
    },
    callingPlanOptions() {
      return this.pbxRegions.map((x) => {
        const hasBought = this.currentBoughtBizPlusPlans.find(p => p.region === x.key);
        let min = 1;
        let minTips = '';
        if (hasBought && this.disableDownSell) {
          min = hasBought.cartInfo.selected_number;
        }

        if (hasBought && this.forNonStandardPriceTips && hasBought.isStandardPrice === false) {
          min = hasBought.cartInfo.selected_number;
        }

        if (hasBought && (this.fromCampaignLink || this.isBulkLicenseAccount)) {
          if (this.plansofBizBundle.totalZOLicenses <= this.minLicense) {
            // biz + biz plus >= this.minLicense
            const selectedPlan = this.currentSelectedBizPlusPlans.find(p => p.region === x.key);
            if (selectedPlan) {
              min = selectedPlan.cartInfo.selected_number;
            }
          }
        }

        const { assignLicenseQuantity = 0 } = _.get(this.mapAssignInfoToBoughtPlans, hasBought?.category, {});
        const selectedPlanByCategory = _.find(this.currentSelectedBizPlusPlans, (p) => p.category === hasBought?.category);
        const selectedQuantity = selectedPlanByCategory?.cartInfo?.selected_number ?? 0;

        if (hasBought && assignLicenseQuantity > 0) {
          min = assignLicenseQuantity;
          if(selectedQuantity <= assignLicenseQuantity) {
            minTips = this.$t('billing.planform.zoom_meeting.less_than_assigned_number_tips_for_expire', [assignLicenseQuantity, 1]);
          }
        }

        min = min >= 1 ? min : 1;

        return ({
          id: x.key,
          name: this.$t(x.label),
          desc: '',
          min: min,
          max: 9999,
          minTips: minTips
        })
      });
    },
    showAddAnotherRegionCTA() {
      const showAddAnotherRegionCTA = this.callingPlanRegions.length !== this.callingPlanOptions.length;
      // can only edit one reginonal plan at the same time for bk account
      if (this.isBulkLicenseAccount) {
        // if has free trial plan, filter it
        const boughtPlansWithoutFreeTrial = this.currentBoughtBizPlusPlans.filter(p => !p.isFreeTrial);
        if (boughtPlansWithoutFreeTrial.length >= 1 || boughtPlansWithoutFreeTrial.length === this.pbxRegions.length) {
          return false;
        }
      }
      if (this.fromCampaignLink) return false;
      return showAddAnotherRegionCTA;
    },
    currentBoughtBizPlusPlans() {
      return this.plansofBizBundle.boughtBizPlusPlan;
    },
    /**
     * Map assign info to bought plans, creating a lookup object with both category and plan code as keys
     * @returns {Object} Object with category and plan_code as keys, both pointing to assign info
     */
    mapAssignInfoToBoughtPlans() {
      const assignInfo = this.assignInfo || {};
      const boughtPlansMap = _.keyBy(this.currentBoughtBizPlusPlans, 'plan_code');

      return _.reduce(assignInfo, (result, assignInfoData, planCode) => {
        const boughtPlan = boughtPlansMap[planCode];

        // Always set plan_code as key
        result[planCode] = assignInfoData;

        // Also set category as key if bought plan exists and has category
        if (boughtPlan?.category) {
          result[boughtPlan.category] = assignInfoData;
        }

        return result;
      }, {});
    },
    currentSelectedBizPlusPlans() {
      return this.plansofBizBundle.selectedBizPlusPlan;
    },
    disableDownSell() {
      return this?.basicConfig?.downSellData?.disableDownSell;
    },
    licenseTips() {
      return _.find(this.callingPlanOptions, (x) => !isEmptyVal(x?.minTips))?.minTips ?? "";
    },
    minLicense() {
      // webinar, large meeting, zoom session/events
      const min = this.bizPlusAddonLicenseTips.min;
      return min >= ZOOM_MEETING_PRO_MIN_LICENSE ? min : ZOOM_MEETING_PRO_MIN_LICENSE;
    },
    bizPlusAddonLicenseTips() {
      return checkBizPlusAddonLicense();
    },
    isStandardPrice() {
      const isStandardPrice = _.find(this.currentSelectedBizPlusPlans, (p) => p.isStandardPrice === false);
      if (isStandardPrice) {
        return (!this.hasAccountDiscount) ? false : true;
      }
      return true;
    },
    forNonStandardPriceTips() {
      return !this.isStandardPrice
        ? this.$t('billing.planform.meeting_license_custom_pricing')
        : '';
    },
    isBulkLicenseAccount() {
      return this.basicConfig?.isBulkLicenseAccount;
    },
    hasBoughtJPPbxPlans(){
      return !! (this.boughtPlans?.filter?.(item => item.region === PBX_REGIONS.JP)?.length)
    }
  },
  watch: {
    currentSelectedBizPlusPlans: {
      handler() {
        this.mx_initCallPlanRegions();
      },
    },
  },
  methods: {
    ...mapActions(['doTrial2PaidAction']),
    async handlePlanDetailsClick() {

    },
    mx_initCallPlanRegions() {
      const zobundlePlans = this.currentSelectedBizPlusPlans;
      // sort by region when click add another region button
      const regionOrder = this.callingPlanRegions.map(p => p.id).filter(p => p);
      const sortRegionsPlan = zobundlePlans.sort((a,b) => {
        const indexA = regionOrder.indexOf(a.region);
        const indexB = regionOrder.indexOf(b.region);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
      this.callingPlanRegions = sortRegionsPlan.map((x) => {
        const { disabled, tooltip, shouldDisabled, disableDelete, disableSwitch, deleteTips } = this.mx_getCallingPlanDisable(x);
        return {
          id: x.region,
          number: x.cartInfo?.selected_number,
          plan_code: x.plan_code,
          disabled: disabled,
          inputTooltip: tooltip,
          shouldDisabled,
          disableSwitch,
          disableDelete,
          deleteTips
        }
      });
    },
    mx_getCallingPlanDisable(plan) {
      const { assignLicenseQuantity = 0 } = _.get(this.mapAssignInfoToBoughtPlans, plan.category, {});
      const hasBought = _.find(this.currentBoughtBizPlusPlans, (p) => p.plan_code === plan.plan_code);
      const hasBoughtByCategory = _.find(this.currentBoughtBizPlusPlans, (p) => p.category === plan.category);

      if (hasBoughtByCategory && assignLicenseQuantity > 0) {
        return {
          disabled: false,
          tooltip: '',
          shouldDisabled: true
        }
      }

      if (hasBought && this.isBulkLicenseAccount) {
        return {
          disabled: false,
          tooltip: this.$t('billing.error.buld_acount_license'),
          shouldDisabled: true
        }
      }
      if (hasBought && this.disableDownSell) {
        return {
          disabled: false,
          tooltip: '',
          shouldDisabled: true
        }
      }

      const disableReason = this.plancodeMapSubscriptionDisabled[plan.plan_code]
      if (disableReason) {
        if (disableReason === PlanDisabledStatusEnum.CANCELED) {
          return {
            disabled: false,
            tooltip: ''
          }
        } else {
          return {
            disabled: true,
            tooltip: this.$t('billing.zobizplus.pending_change')
          };
        }
      }
      return {
        disabled: false,
        tooltip: ''
      }
    },
  },
  mounted() {
    this.doTrial2PaidAction({ couponCode: this.basicConfig?.pendingFreeTrialCoupon });
  },
};
</script>
